import { useEffect, useState } from "react";
import { api, RAYG, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Trash } from "@phosphor-icons/react";

export function BenefitsTracking() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ period: "Y1 Q4", amount: "", category: "Cost savings", note: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/benefits").then((r) => setData(r.data)); }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: d } = await api.post("/benefits", { ...form, amount: Number(form.amount) });
      setData(d);
      setForm({ ...form, amount: "", note: "" });
      toast.success("Actual benefit recorded — added to audit trail");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setSaving(false);
  };

  const remove = async (id) => {
    try {
      const { data: d } = await api.delete(`/benefits/${id}`);
      setData(d);
      toast.success("Entry removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!data) return null;
  const { summary } = data;
  const pct = summary.realisation_pct;
  const kpis = [
    ["Forecast to date", summary.forecast_to_date != null ? `R${summary.forecast_to_date}m` : "—"],
    ["Realised to date", `R${summary.realised_to_date}m`],
    ["Realisation", pct != null ? `${pct}%` : "—"],
    ["Variance", `${summary.variance >= 0 ? "+" : ""}R${summary.variance}m`],
  ];

  return (
    <div className="mt-8" data-testid="benefits-tracking-section">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">Benefits Realisation</p>
          <h2 className="font-display text-2xl font-bold tracking-tight">Forecast vs Actual — {data.scenario_name}</h2>
        </div>
        <span className="text-[11px] text-black/50">Forecast derived from the {data.scenario_approved ? "approved" : "recommended"} scenario's business case (quarterly, {data.currency}). Tracked through {summary.latest_period || "—"}.</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        {kpis.map(([l, v], i) => (
          <div key={l} className="bg-[#0A0A0A] text-white rounded-sm p-4" data-testid={`benefit-kpi-${i}`}>
            <p className="text-[10px] uppercase tracking-[0.15em] text-white/60">{l}</p>
            <p className="font-display text-3xl font-black mt-1"
              style={{ color: i >= 2 && pct != null ? RAYG(pct) : "#D4AF37" }}>{v}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-black/10 rounded-sm p-5 mb-5" data-testid="benefits-chart">
        <p className="text-[11px] uppercase tracking-[0.15em] text-black/50 mb-3">Cumulative benefits ({data.currency}) — gold line is business-case forecast, bars are quarterly actuals</p>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={data.periods}>
            <XAxis dataKey="period" tick={{ fontSize: 9 }} interval={1} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v, name) => [v == null ? "—" : `R${v}m`, { forecast_cum: "Forecast (cum)", actual_cum: "Actual (cum)", actual: "Actual (quarter)" }[name] || name]} />
            <Legend formatter={(v) => ({ forecast_cum: "Forecast cumulative", actual_cum: "Actual cumulative", actual: "Quarterly actual" }[v] || v)} />
            <Bar dataKey="actual" fill="#3B82F6" radius={[2, 2, 0, 0]} />
            <Line type="monotone" dataKey="forecast_cum" stroke="#D4AF37" strokeWidth={2.5} strokeDasharray="6 3" dot={false} />
            <Line type="monotone" dataKey="actual_cum" stroke="#22C55E" strokeWidth={2.5} dot={{ r: 3 }} connectNulls={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <form onSubmit={add} className="bg-white border border-black/10 rounded-sm p-5" data-testid="benefit-entry-form">
          <p className="text-[11px] uppercase tracking-[0.15em] text-black/50 mb-3">Record realised benefit (audited)</p>
          <div className="grid grid-cols-2 gap-3">
            <select value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} data-testid="benefit-period-select"
              className="border border-black/15 rounded-sm px-2 py-2 text-sm bg-white">
              {data.period_options.map((p) => <option key={p}>{p}</option>)}
            </select>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="benefit-category-select"
              className="border border-black/15 rounded-sm px-2 py-2 text-sm bg-white">
              {data.categories.map((c) => <option key={c}>{c}</option>)}
            </select>
            <input required type="number" step="0.1" min="0" max="1000" placeholder="Amount (R millions)" value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="benefit-amount-input"
              className="border border-black/15 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[#D4AF37]" />
            <input placeholder="Evidence note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
              data-testid="benefit-note-input"
              className="border border-black/15 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[#D4AF37]" />
          </div>
          <button type="submit" disabled={saving} data-testid="benefit-submit-btn"
            className="mt-3 text-xs bg-[#0A0A0A] text-[#D4AF37] rounded-sm px-4 py-2.5 font-semibold hover:bg-black/85 transition-colors disabled:opacity-50">
            {saving ? "Recording…" : "Record benefit"}
          </button>
        </form>

        <div className="bg-white border border-black/10 rounded-sm overflow-hidden">
          <div className="px-4 py-2.5 bg-[#0A0A0A] text-[#D4AF37] text-[11px] uppercase tracking-[0.15em]">Recorded actuals</div>
          <div className="max-h-[220px] overflow-y-auto divide-y divide-black/5">
            {data.entries.length === 0 && <p className="p-4 text-[12px] text-black/45">No actuals recorded yet — add the first realised benefit to start tracking against forecast.</p>}
            {data.entries.map((e) => (
              <div key={e.id} className="px-4 py-2.5 flex items-center justify-between text-[12px]" data-testid="benefit-entry-row">
                <div>
                  <span className="font-mono font-medium">{e.period}</span>
                  <span className="mx-2 font-semibold">R{e.amount}m</span>
                  <span className="text-black/55">{e.category}</span>
                  {e.note && <p className="text-[11px] text-black/45">{e.note}</p>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-[10px] text-black/40">{e.recorded_by}</span>
                  <button onClick={() => remove(e.id)} data-testid={`benefit-delete-${e.id}`}
                    className="text-black/35 hover:text-red-500 transition-colors"><Trash size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
